import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

function AkrecniDisk() {
  const particleGroupRef = useRef();
  const numParticles = 250;
  const trailLength = 10;

  // 🌠 **Generování částic pro akreční disk**
  const particles = useMemo(() => {
    return new Array(numParticles).fill().map(() => {
      const angle = Math.random() * Math.PI * 2;
      const radius = THREE.MathUtils.lerp(6, 10, Math.random());
      const speed = THREE.MathUtils.mapLinear(radius, 6, 10, 0.5, 10);

      return {
        position: new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius),
        prevPosition: new THREE.Vector3(), // 🆕 Uložení předchozí pozice
        speed,
        angle,
        radius,
        color: new THREE.Color(`hsl(192, 100%, ${THREE.MathUtils.lerp(40, 80, Math.random())}%)`),
        trail: new Array(trailLength).fill(null).map(() => new THREE.Vector3()),
      };
    });
  }, []);

  return (
    <group ref={particleGroupRef} position={[0, 0, 0]}>
      {particles.map((p, i) => (
        <ParticleTrail key={i} data={p} />
      ))}
    </group>
  );
}

// ✨ **Každá částice má vlastní stopu s interpolací pohybu**
function ParticleTrail({ data }) {
  const meshRef = useRef();
  const trailRef = useRef();

  const trailPositions = useMemo(() => new Float32Array(data.trail.length * 3), []);
  const trailColors = useMemo(() => new Float32Array(data.trail.length * 4), []);

  useFrame(() => {
    // 🆕 Uložíme starou pozici před výpočtem nové
    data.prevPosition.copy(data.position);

    // ✅ Aktualizace pozice částice
    data.angle += data.speed * 0.01;
    const x = Math.cos(data.angle) * data.radius;
    const z = Math.sin(data.angle) * data.radius;
    data.position.set(x, 0, z);

    // ✅ Interpolace mezi starou a novou pozicí
    const midPoint = new THREE.Vector3().lerpVectors(data.prevPosition, data.position, 0.5);

    // ✅ Posun trailu, přidáme interpolovaný bod
    for (let j = data.trail.length - 1; j > 1; j--) {
      data.trail[j].copy(data.trail[j - 2]);
    }
    data.trail[1].copy(midPoint); // Střední bod pro plynulejší stopu
    data.trail[0].copy(data.position); // Aktuální pozice částice

    // ✅ Aktualizace bufferu pro stopu
    data.trail.forEach((pos, index) => {
      const idx = index * 3;
      trailPositions[idx] = pos.x;
      trailPositions[idx + 1] = pos.y;
      trailPositions[idx + 2] = pos.z;

      const alpha = 1 - index / data.trail.length;
      const colorIdx = index * 4;
      trailColors[colorIdx] = data.color.r;
      trailColors[colorIdx + 1] = data.color.g;
      trailColors[colorIdx + 2] = data.color.b;
      trailColors[colorIdx + 3] = alpha;
    });

    if (trailRef.current) {
      trailRef.current.geometry.attributes.position.needsUpdate = true;
      trailRef.current.geometry.attributes.color.needsUpdate = true;
    }

    if (meshRef.current) {
      meshRef.current.position.copy(data.position);
    }
  });

  return (
    <group>
      <mesh ref={meshRef} position={data.position}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial emissive={data.color} emissiveIntensity={0.8} />
      </mesh>
      <line ref={trailRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" array={trailPositions} itemSize={3} count={data.trail.length} usage={THREE.DynamicDrawUsage} />
          <bufferAttribute attach="attributes-color" array={trailColors} itemSize={4} count={data.trail.length} usage={THREE.DynamicDrawUsage} />
        </bufferGeometry>
        <lineBasicMaterial vertexColors transparent opacity={0.8} />
      </line>
    </group>
  );
}

export default AkrecniDisk;